import { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';
import { ClipboardCheck, Mail, Lock, Package } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function StockCheckAuth() {
  const { user, signIn, loading: authLoading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get the token from redirect query param
  const redirectToken = searchParams.get('token');
  const redirectPath = redirectToken ? `/conferencia/${redirectToken}` : null;

  // Check if user is already authenticated
  useEffect(() => {
    if (user && !authLoading && redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [user, authLoading, redirectPath, navigate]);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      await signIn(email, password);
      // After sign in, the useEffect will handle redirect
    } catch (error) {
      toast({
        title: 'Erro no login',
        description: 'Verifique suas credenciais e tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking auth status
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-emerald-500/20 p-4 rounded-xl">
            <ClipboardCheck className="h-12 w-12 text-emerald-500 animate-pulse" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
          </div>
          <p className="text-emerald-200/70 text-sm">Verificando...</p>
        </div>
      </div>
    );
  }

  // Already logged in and has redirect path
  if (user && redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <ClipboardCheck className="absolute top-10 left-10 h-16 w-16 text-emerald-500/10 rotate-12" />
        <ClipboardCheck className="absolute top-20 right-20 h-24 w-24 text-teal-500/10 -rotate-12" />
        <ClipboardCheck className="absolute bottom-20 left-20 h-20 w-20 text-emerald-500/10 rotate-6" />
        <ClipboardCheck className="absolute bottom-10 right-10 h-16 w-16 text-teal-500/10 -rotate-6" />
        <Package className="absolute top-1/4 right-10 h-12 w-12 text-emerald-500/10" />
        <Package className="absolute bottom-1/4 left-10 h-12 w-12 text-teal-500/10" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Logo with clipboard */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="bg-emerald-500/20 p-3 rounded-xl">
              <ClipboardCheck className="h-10 w-10 text-emerald-500" />
            </div>
            <img 
              src="/gonx-logo.png" 
              alt="GOnX Logo" 
              className="h-24 w-auto"
            />
          </div>
          
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <ClipboardCheck className="h-4 w-4" />
            CONFERÊNCIA DE ESTOQUE
          </div>
          
          <h1 className="text-2xl font-bold text-white">GOnX Conferência</h1>
        </div>

        {/* Auth Card */}
        <Card className="shadow-2xl border-emerald-500/20 bg-slate-800/90 backdrop-blur-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-white flex items-center justify-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-emerald-500" />
              Entrar no Sistema
            </CardTitle>
            <CardDescription className="text-emerald-200/70">
              Use suas credenciais de acesso
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-emerald-100">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-emerald-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10 bg-slate-700/50 border-emerald-500/30 text-white placeholder:text-slate-400 focus:border-emerald-500"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-emerald-100">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-emerald-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 bg-slate-700/50 border-emerald-500/30 text-white placeholder:text-slate-400 focus:border-emerald-500"
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="link"
                    className="text-sm p-0 h-auto text-emerald-300 hover:text-emerald-400"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Esqueci minha senha
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-12 text-base gap-2"
                disabled={isSubmitting}
              >
                <ClipboardCheck className="h-5 w-5" />
                {isSubmitting ? 'Entrando...' : 'Entrar no Sistema'}
              </Button>
            </form>

            <p className="text-xs text-emerald-200/50 text-center mt-6">
              Conferentes são cadastrados pelo administrador.
              <br />
              Entre em contato se precisar de acesso.
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-emerald-200/50 flex items-center justify-center gap-2">
          <ClipboardCheck className="h-4 w-4" />
          <p>© 2024 GOnX Conferência - Controle de Estoque</p>
          <ClipboardCheck className="h-4 w-4" />
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword}
      />
    </div>
  );
}
